/* eslint-disable react/prop-types */
import {
  type FC,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useRef,
  useImperativeHandle,
  type MutableRefObject,
  type TouchEvent,
  useState,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames/bind';
import { FAIcon } from '../Icons/Icons';
import { Theme, DEFAULT_PORTAL_CONTAINER_ID } from '../types/enums';
import { getContainerPortalId, isMobileDevice } from '../utils';
import { useFreezeWindowScroll } from '../hooks';
import { type DrawerPropsTypes } from './types/propTypes';
import { DrawerDirections, DrawerOpenAt } from './types/enums';
import styles from './Drawer.module.scss';

const cx = classNames.bind(styles);

const handleMobileContentScroll = (contentRef: MutableRefObject<HTMLDivElement>) => {
  if (isMobileDevice()) {
    const { activeElement } = document;
    if (
      contentRef?.current?.contains(activeElement) &&
      (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA')
    ) {
      (activeElement as HTMLElement).blur();
    }
  }
};

const Drawer: FC<DrawerPropsTypes> = forwardRef<HTMLDivElement, DrawerPropsTypes>(
  (
    {
      header,
      subHeader,
      footer,
      onClose,
      closeIcon,
      children,
      hideHeaderDivider,
      closeOnBackdropClick,
      className = '',
      bottomGap = 68,
      drawerWidth,
      direction,
      fullDraggable = false,
      useReactPortal = false,
      hideDrawer,
      portalContainerId = DEFAULT_PORTAL_CONTAINER_ID,
      drawerOpenAt = DrawerOpenAt.DEFAULT,
    },
    ref,
  ) => {
    const [movingWithTouch, setMovingWithTouch] = useState<boolean>(false);
    const [startY, setStartY] = useState<number>(0);
    const drawerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const drawerContentRef = useRef() as MutableRefObject<HTMLDivElement>;
    useImperativeHandle(ref, () => drawerRef.current);
    const drawerHandleRef = useRef() as MutableRefObject<HTMLDivElement>;

    const onCloseCallback = (e: ReactKeyboardEvent | ReactMouseEvent | TouchEvent) => {
      if (hideDrawer !== false) {
        drawerRef.current.className += styles['close-drawer-animation'];
        if (onClose) {
          setTimeout(() => {
            onClose(e);
          }, 300);
        }
      } else {
        onClose?.(e);
      }
    };
    useEffect(() => {
      if (hideDrawer && drawerRef.current) {
        drawerRef.current.className += styles['close-drawer-animation'];
      }
    }, [hideDrawer]);

    const onBackdropClick = (e: ReactKeyboardEvent | ReactMouseEvent) => {
      if (closeOnBackdropClick) {
        onCloseCallback(e);
      }
    };

    const directionClass = direction ? styles[`direction-${direction}`] : '';

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const dragLimit = fullDraggable ? styles.topLimit : startY;
      if (movingWithTouch && (e.changedTouches[0]?.clientY as number) > (dragLimit as unknown as number)) {
        drawerRef.current.style.top = `${e.changedTouches[0]?.clientY}px`;
      }
    };
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      setMovingWithTouch(true);
      if (!startY) {
        setStartY(e.currentTarget.getBoundingClientRect().top);
      }
    };
    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      setMovingWithTouch(false);
      if ((e.changedTouches[0]?.clientY as number) > 0.75 * window.innerHeight) {
        onCloseCallback(e);
      } else {
        // 36px is top drag limit, upto which drawer can be drag.
        let dragTopLimit = 'unset';
        if (fullDraggable) {
          dragTopLimit = `${
            (e.changedTouches[0]?.clientY as number) > startY
              ? startY
              : (e.changedTouches[0]?.clientY as number) <= (styles.topLimit as unknown as number)
              ? styles.topLimit
              : (e.changedTouches[0]?.clientY as number)
          }px`;
        }
        if (drawerOpenAt === DrawerOpenAt.DEFAULT) {
          drawerRef.current.style.top = dragTopLimit;
        }
        drawerRef.current.style.bottom = bottomGap !== undefined ? `${bottomGap}px` : 'unset';
      }
    };

    useFreezeWindowScroll();

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (drawerContentRef.current) {
        drawerContentRef.current.addEventListener('touchmove', () => handleMobileContentScroll(drawerContentRef));
        return () => {
          drawerContentRef.current?.removeEventListener('touchmove', () => handleMobileContentScroll(drawerContentRef));
        };
      }
    }, [drawerContentRef.current]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (drawerRef?.current) {
        const element = drawerRef.current;
        element.style.setProperty('--bottomGap', `${bottomGap}px`);
      }
    }, []);

    const CBodyClassname = cx('rt-drawer', {
      'full-height-drawer': direction === DrawerDirections.FULLHEIGHT,
    });
    const CHeaderClassname = cx('drawer-header', { 'drawer-header-divider': !hideHeaderDivider });
    const CFooterClassname = cx('drawer-footer', { 'full-height-drawer': direction === DrawerDirections.FULLHEIGHT });
    const drawerBody = (
      <>
        <div
          className={`${styles['drawer-container']} ${directionClass} ${
            fullDraggable ? styles['full-height-draggable'] : ''
          } ${styles[drawerOpenAt]} ${className}`}
          id="nextgen-drawer-component"
          ref={drawerRef}
        >
          <div className={CBodyClassname} data-testid="base-testid" style={drawerWidth ? { width: drawerWidth } : {}}>
            <div
              className={styles['mobile-tophandle']}
              role="presentation"
              data-testid="handle-testid"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
              ref={drawerHandleRef}
            >
              <span></span>
            </div>
            {header && (
              <section className={CHeaderClassname} data-testid="header-testid">
                {header}
                {closeIcon && (
                  <div className={styles['icon-section']}>
                    <FAIcon
                      className={styles['close-icon']}
                      data-testid="close-icon-testid"
                      iconName={'close'}
                      tabIndex={-1}
                      theme={Theme.BLACK}
                      onClick={onCloseCallback}
                    />
                  </div>
                )}
              </section>
            )}
            {subHeader && (
              <section className={styles['drawer-subheader']} data-testid="subheader-testid">
                {subHeader}
              </section>
            )}
            <section
              className={styles['drawer-body-content']}
              data-testid="body-testid"
              data-allow-scroll="true"
              ref={drawerContentRef}
            >
              {children}
            </section>
            {footer && (
              <section className={CFooterClassname} data-testid="full-height-testid">
                {footer}
              </section>
            )}
          </div>
        </div>

        <div
          className={styles['backdrop']}
          aria-hidden="true"
          onClick={onBackdropClick}
          data-testid="backdrop-testid"
        ></div>
      </>
    );

    const portalContainer = getContainerPortalId(portalContainerId);

    return useReactPortal && portalContainer ? createPortal(drawerBody, portalContainer) : drawerBody;
  },
);
Drawer.displayName = 'Drawer';
export default Drawer;
export { DrawerDirections, DrawerOpenAt, DEFAULT_PORTAL_CONTAINER_ID };
export type { DrawerPropsTypes };
